import { dayjs } from './dayjs.setup';

export const toGermanDate = (date: string) => {
  return dayjs(date, 'YYYY-MM-DD', true).format('DD.MM.YYYY');
};

/** Returns a German-formatted date (`dd.mm.yyyy`) or `'—'` for empty/undefined input. */
export const formatGermanDate = (date: string | undefined | null): string =>
  date ? toGermanDate(date) : '—';

export const dateOrNull = (date: string | null | undefined) => {
  if (!date) return null;

  return fromIsoDateString(date);
};

export const fromIsoDateString = (date: string) => {
  return dayjs(date, 'YYYY-MM-DD', true);
};

export const toIsoDateString = (date: dayjs.Dayjs) => {
  return date.format('YYYY-MM-DD');
};

export const timestampToDate = (timestamp: string) => {
  return dayjs(timestamp).format('YYYY-MM-DD');
};

export const germanDayJsDate = (date: string | null) => {
  return dayjs(date, 'DD.MM.YYYY');
};

export const dateOrToday = (date: string | undefined) => {
  return dateOrFallback(date, dayjs());
};

export const dateOrFallback = (date: string | undefined, fallback: dayjs.Dayjs) => {
  return date ? dayjs(date, 'YYYY-MM-DD', true) : fallback;
};

/**
 * Approach of a general comparator of elements with a "datum" property.
 * In reality this is also abused as a general comparator for any date strings by creating dummy object
 * which only hold the needed "datum" property.
 * Maybe a sign to find a better approach by only comparing strings.
 * **/
export const dateComparator = <T extends { datum: string | undefined | null }>(
  a: T,
  b: T,
  interpretNullableAsGreater = true,
) => {
  const aHasDate = a.datum !== undefined && a.datum !== null;
  const bHasDate = b.datum !== undefined && b.datum !== null;

  if (!aHasDate && !bHasDate) return 0;
  if (!aHasDate) return interpretNullableAsGreater ? 1 : -1;
  if (!bHasDate) return interpretNullableAsGreater ? -1 : 1;

  const dateA = dayjs(a.datum);
  const dateB = dayjs(b.datum);

  if (!dateA.isValid() && !dateB.isValid()) return 0;
  if (!dateA.isValid()) return 1;
  if (!dateB.isValid()) return -1;

  return dateA.isBefore(dateB) ? -1 : dateA.isAfter(dateB) ? 1 : 0;
};

/**
 * Given a set of intervals, we
 * sort the intervals by their left bound (start date)
 * initialize a result list of intervals `R`
 * iterate over the sorted intervals
 * on each iteration, consider the current interval `C` and the previous `P` (last added to `R`)
 *    * if `C` starts before `P` ends, we merge `P` and `C`
 *      (set its end date to the maximum of the end of `C` and the end of `P`)
 *      and adjust the last entry of `R` respectively
 *    * otherwise, add `C` to the result list
 *
 * Finally, we return the sum of all lengths (in days) of all merged intervals.
 *
 * **Visual example:**
 * <pre>
 * |------|
 *     |-----|
 *                |------|
 *                  |---|
 * </pre>
 * Going from left to right in time (corresponds top to bottom in the sorted intervals in this case),
 * whenever a left bar is before the previous right bar, we can just merge those to intervals by
 * removing the current one and setting the previous right bar to whichever right bar is more to
 * the right, and still maintain the same amount of days covered by intervals.
 *
 */
export const mergedIntervals = (intervals: { von: dayjs.Dayjs; bis: dayjs.Dayjs }[]) => {
  const sortedIntervals = [...intervals].sort((i1, i2) => i1.von.valueOf() - i2.von.valueOf());

  const mergedIntervals: { von: dayjs.Dayjs; bis: dayjs.Dayjs }[] = [];

  for (let currentInterval of sortedIntervals) {
    const previousInterval = mergedIntervals.at(-1);
    if (previousInterval && currentInterval.von.isSameOrBefore(previousInterval.bis)) {
      previousInterval.bis = dayjs.max(previousInterval.bis, currentInterval.bis);
    } else {
      mergedIntervals.push(currentInterval);
    }
  }

  return mergedIntervals;
};

export const calculateTotalDayCount = (
  intervals: { von: dayjs.Dayjs; bis: dayjs.Dayjs }[],
): number => {
  const merged = mergedIntervals(intervals);
  return merged.reduce((total, au) => total + au.bis.diff(au.von, 'day') + 1, 0);
};
